import { ChartListItem, LendingEquipmentUsesVolumeByProductListChart } from '@frontend/chart/components';
import { CommonMessage } from '@frontend/lang';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { InsightId } from '../insight-registry/insight-ids';
import { useLendingEquipmentUsesVolumeByProductList } from './use-lending-equipment-uses-volume-by-product-list';

export interface LendingEquipmentUsesVolumeByProductListProps {
    accountId: string;
    width?: InsightCardWidth;
}

type UsageRow = { type: 'section'; label: ReactNode } | { type: 'item'; item: ChartListItem; tone: 'success' | 'danger' | 'muted' };

export const LendingEquipmentUsesVolumeByProductList = ({ accountId, width }: LendingEquipmentUsesVolumeByProductListProps) => {
    const { formatMessage } = useIntl();
    const unknownLabel = formatMessage({
        id: 'Objects.UNKNOWN.DEFAULT',
        description: 'Label for unknown',
        defaultMessage: 'Unknown'
    });
    const { highestItems, lowestItems, detailItems, isLoading, isDetailMode, hasData } = useLendingEquipmentUsesVolumeByProductList(accountId, unknownLabel);

    const productHeader = CommonMessage.OBJECTS.PRODUCT.DEFAULT;
    const usesHeader = CommonMessage.OBJECTS.USE.PLURAL;

    const rows = useMemo((): UsageRow[] => {
        if (isDetailMode) {
            return detailItems.map((item) => ({ type: 'item' as const, item, tone: 'muted' as const }));
        }

        const result: UsageRow[] = [];

        if (highestItems.length > 0) {
            result.push({
                type: 'section',
                label: (
                    <FormattedMessage
                        id='platform.analytics.insights.lending-equipment-uses-volume-by-product-list.highest'
                        description='Section heading above the products with the highest use count on lending insights'
                        defaultMessage='Most used'
                    />
                )
            });
            result.push(...highestItems.map((item) => ({ type: 'item' as const, item, tone: 'success' as const })));
        }

        if (lowestItems.length > 0) {
            result.push({
                type: 'section',
                label: (
                    <FormattedMessage
                        id='platform.analytics.insights.lending-equipment-uses-volume-by-product-list.lowest'
                        description='Section heading above the products with the lowest use count on lending insights'
                        defaultMessage='Least used'
                    />
                )
            });
            result.push(...lowestItems.map((item) => ({ type: 'item' as const, item, tone: 'danger' as const })));
        }

        return result;
    }, [isDetailMode, detailItems, highestItems, lowestItems]);

    const badgeClassName = (tone: 'success' | 'danger' | 'muted'): string => {
        if (tone === 'success') return 'bg-status-success';
        if (tone === 'danger') return 'bg-status-danger';
        return 'bg-brand-muted';
    };

    return (
        <InsightCard
            insightId={InsightId.LENDING_USAGE_COUNT_LAST_MONTH}
            chartKey={LendingEquipmentUsesVolumeByProductListChart.definition.key}
            width={width}
            title={
                <FormattedMessage
                    id='platform.analytics.insights.lending-equipment-uses-volume-by-product-list.title'
                    description='Title for the lending insights list showing product use counts over the last 30 days'
                    defaultMessage='Usage count in last month'
                />
            }
            subtitle={
                <FormattedMessage
                    id='widget.last-30-days'
                    defaultMessage='last 30 days'
                />
            }
            isLoading={isLoading}>
            {!hasData ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : (
                <div className='min-h-0 w-full flex-1 overflow-y-auto'>
                    <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                        <div className='w-full px-4 py-3'>{productHeader}</div>
                        <div className='w-[120px] shrink-0 px-4 py-3 text-right'>{usesHeader}</div>
                    </div>
                    {rows.map((row, index) =>
                        row.type === 'section' ? (
                            <div
                                key={`section-${index}`}
                                className='border-border-default text-foreground-default border-b px-4 py-3 text-base font-semibold'>
                                {row.label}
                            </div>
                        ) : (
                            <div
                                key={row.item.key}
                                className='border-border-default flex h-11 items-center gap-4 border-b'>
                                <div className='text-foreground-default flex w-full min-w-0 items-center px-4 text-sm font-medium'>
                                    <span className='truncate'>{row.item.name}</span>
                                </div>
                                <div className='flex w-[120px] shrink-0 justify-end pe-4'>
                                    <span
                                        className={`text-background-surface inline-flex min-w-8 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold ${badgeClassName(row.tone)}`}>
                                        {row.item.value}
                                    </span>
                                </div>
                            </div>
                        )
                    )}
                </div>
            )}
        </InsightCard>
    );
};
